/**
 * Admin user-detail surface. Solo-founder day-1 support workflow:
 *
 *   1. Type an email / phone / userId → see matching users.
 *   2. Click a row → load the full dossier (profile, status, balance,
 *      recent ledger, recent reports, active ban).
 *   3. Act on the user: ban (with reason), unban (with reason), grant
 *      or revoke credits (with reason).
 *
 * Every action is gated server-side on role + writes an audit_log row
 * with the acting admin's id. Voice rule: matter-of-fact prose, no
 * security-theater language. The reason field is required because that
 * audit row is what we'll point to in 6 months when someone asks "why
 * was this account banned" — a 2-character `oops` is useless.
 */

import { Badge, type BadgeVariant } from '@funkihoo/ui-web';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  type AuditLogEntry,
  type ProfileFull,
  type UserDetail,
  useBanUserMutation,
  useGrantCreditsMutation,
  useUnbanUserMutation,
  useUserAuditLogQuery,
  useUserDetailQuery,
  useUserProfileFullQuery,
  useUserSearchQuery,
} from '../api/users.js';
import { trackAdminEvent } from '../lib/posthog.js';

export function UserDetailRoute() {
  const [searchParams, setSearchParams] = useSearchParams();
  const idFromUrl = searchParams.get('id');
  const [query, setQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(idFromUrl);

  // Deep-link entry: when /users?id=<uuid> changes (e.g. clicking
  // a reporter/reported chip in the moderation queue navigates the
  // user here), drop whatever was selected before and load the
  // deep-linked dossier instead.
  useEffect(() => {
    if (idFromUrl && idFromUrl !== selectedUserId) {
      setSelectedUserId(idFromUrl);
    }
  }, [idFromUrl, selectedUserId]);

  const search = useUserSearchQuery(activeQuery);
  const detail = useUserDetailQuery(selectedUserId);

  // Fire a PostHog event each time a distinct dossier opens so we
  // can track admin workload + spot dossiers opened repeatedly (often
  // signal an ongoing investigation). User id is the admin-side
  // target uuid — not the admin's own id, which PostHog already
  // identifies separately.
  useEffect(() => {
    if (selectedUserId) {
      trackAdminEvent('admin.dossier.opened', { targetUserId: selectedUserId });
    }
  }, [selectedUserId]);

  function selectUser(id: string) {
    setSelectedUserId(id);
    // Keep the URL in sync so a deep-link reload / share lands on
    // the same dossier.
    setSearchParams({ id }, { replace: true });
  }

  return (
    <section className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold text-neutral-900">Users</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Look up a user by email, phone (E.164), or user id. Every action below is audit-logged
          with your admin id and the reason you provide. PII access is role-gated.
        </p>
      </header>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setActiveQuery(query.trim());
          setSelectedUserId(null);
        }}
      >
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="email, +E.164 phone, or UUID"
          className="flex-1 rounded-md border border-neutral-300 px-3 py-2 text-sm"
        />
        <button
          type="submit"
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          Search
        </button>
      </form>

      {activeQuery && (
        <section>
          <h2 className="text-sm font-medium text-neutral-700">Results</h2>
          {search.isLoading && <p className="mt-2 text-sm text-neutral-500">Searching…</p>}
          {search.isError && (
            <p className="mt-2 text-sm text-danger-600">Search failed. Try again.</p>
          )}
          {search.data && search.data.results.length === 0 && (
            <p className="mt-2 text-sm text-neutral-500">No users match.</p>
          )}
          {search.data && search.data.results.length > 0 && (
            <ul className="mt-2 divide-y divide-neutral-200 rounded-md border border-neutral-200">
              {search.data.results.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => selectUser(r.id)}
                    className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-neutral-50 ${
                      selectedUserId === r.id ? 'bg-primary-50' : ''
                    }`}
                  >
                    <span>
                      <span className="font-medium text-neutral-900">{r.email}</span>
                      <span className="ml-2 text-neutral-500">
                        {r.phoneE164 ?? 'no phone'} · {r.status}
                      </span>
                    </span>
                    <span className="font-mono text-xs text-neutral-400">{r.id.slice(0, 8)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {selectedUserId && (
        <section className="rounded-md border border-neutral-200 bg-white p-4">
          {detail.isLoading && <p className="text-sm text-neutral-500">Loading dossier…</p>}
          {detail.isError && (
            <p className="text-sm text-danger-600">Could not load dossier. Try refreshing.</p>
          )}
          {detail.data && <UserDossier dossier={detail.data} />}
        </section>
      )}
    </section>
  );
}

function UserDossier({ dossier }: { dossier: UserDetail }) {
  const { user, profile, creditBalance, recentLedger, recentReports, activeBan } = dossier;
  const banMut = useBanUserMutation();
  const unbanMut = useUnbanUserMutation();
  const grantMut = useGrantCreditsMutation();
  // Two extra surfaces the founder asked for 2026-06-18: the full
  // profile (so they can spot off-platform contact info, weird bio
  // content, photo issues — the headline dossier was too sparse to
  // see anything) and the audit log (which admin took which action
  // when). Both query the same dossier user-id; React Query
  // dedupes if they happen to be open simultaneously.
  const profileFull = useUserProfileFullQuery(user.id);
  const audit = useUserAuditLogQuery(user.id);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-neutral-900">
            {profile?.displayName ?? user.email}
          </h2>
          <p className="mt-1 text-sm text-neutral-600">
            {user.email} · {user.phoneE164 ?? 'no phone'} · joined{' '}
            {new Date(user.createdAt).toLocaleDateString()}
          </p>
          <p className="mt-1 text-sm text-neutral-600">
            Last seen{' '}
            {user.lastSeenAt
              ? new Date(user.lastSeenAt).toLocaleString()
              : 'never (no authed request yet)'}
          </p>
          <p className="mt-1 font-mono text-xs text-neutral-400">{user.id}</p>
          {/* Special-account flags + open-queue tallies. Stays scannable
              at one row; the deep-dive pages (Tickets, Appeals) live
              in the left nav. Keeping the counts here prevents support
              from acting on a user with an open ticket they didn't
              know about. */}
          {(user.isFounderGrant ||
            user.demoReviewer ||
            user.openSupportTickets > 0 ||
            user.pendingBanAppeals > 0) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {user.isFounderGrant && <Badge variant="info">founder grant</Badge>}
              {user.demoReviewer && <Badge variant="warning">demo reviewer</Badge>}
              {user.openSupportTickets > 0 && (
                <Badge variant="warning">
                  {user.openSupportTickets} open ticket{user.openSupportTickets === 1 ? '' : 's'}
                </Badge>
              )}
              {user.pendingBanAppeals > 0 && <Badge variant="danger">appeal pending</Badge>}
            </div>
          )}
        </div>
        <div className="text-right">
          <StatusBadge status={user.status} />
          <p className="mt-1 text-sm text-neutral-600">
            Risk: {user.riskScore}
            {user.riskSignalsCount > 0
              ? ` · ${user.riskSignalsCount} signal${user.riskSignalsCount === 1 ? '' : 's'}`
              : ''}
          </p>
          <p className="mt-1 text-sm text-neutral-900">Balance: {creditBalance} cr</p>
          {user.signupTrialPerksUntil && (
            <p className="mt-1 text-neutral-500 text-xs">
              Trial perks until {new Date(user.signupTrialPerksUntil).toLocaleDateString()}
            </p>
          )}
        </div>
      </div>

      {profile ? (
        <Section title="Profile">
          <dl className="grid grid-cols-2 gap-2 text-sm">
            <dt className="text-neutral-500">Gender</dt>
            <dd>{profile.gender ?? '—'}</dd>
            <dt className="text-neutral-500">DOB</dt>
            <dd>{profile.dateOfBirth ?? '—'}</dd>
            <dt className="text-neutral-500">Country</dt>
            <dd>{profile.countryOfResidenceIso ?? '—'}</dd>
            <dt className="text-neutral-500">Marital status</dt>
            <dd>{profile.maritalStatus ?? '—'}</dd>
          </dl>
        </Section>
      ) : (
        <Section title="Profile">
          <p className="text-sm text-neutral-500">No profile (user hasn't completed the wizard).</p>
        </Section>
      )}

      {activeBan ? (
        <Section title="Active ban">
          <p className="text-sm text-neutral-900">
            <span className="font-medium">{activeBan.kind}</span> · banned{' '}
            {new Date(activeBan.bannedAt).toLocaleString()}
          </p>
          <p className="mt-1 text-sm text-neutral-600">{activeBan.reason}</p>

          {/* Auto-ban audit trail (Plan §Trust & Safety §Auto-ban
              audit-trail enrichment). Only renders when this is an
              auto-ban with hydrated triggering reports. Manual bans
              and pre-2026-06 auto-bans show no list (null). */}
          {activeBan.triggeringReports && activeBan.triggeringReports.length > 0 ? (
            <div className="mt-3 rounded-md border border-neutral-200 bg-neutral-50/60 p-3">
              <p className="font-medium text-neutral-900 text-xs">
                Triggered by {activeBan.triggeringReports.length} report
                {activeBan.triggeringReports.length === 1 ? '' : 's'}
              </p>
              <ul className="mt-2 space-y-1.5">
                {activeBan.triggeringReports.map((r) => (
                  <li key={r.id} className="text-neutral-700 text-xs">
                    <span className="font-medium">{r.reason}</span>
                    <span className="text-neutral-500"> · {r.status}</span>
                    <span className="text-neutral-500">
                      {' '}
                      · {new Date(r.createdAt).toLocaleString()}
                    </span>
                    <span className="text-neutral-400"> · report id {r.id.slice(0, 8)}…</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </Section>
      ) : null}

      <Section title="Actions">
        {user.status === 'banned' ? (
          <ReasonPromptButton
            label="Unban"
            confirmLabel="Confirm unban"
            placeholder="Why is this ban being lifted?"
            pending={unbanMut.isPending}
            error={unbanMut.error}
            onConfirm={(reason) => unbanMut.mutate({ userId: user.id, reason })}
          />
        ) : (
          <ReasonPromptButton
            label="Ban user"
            confirmLabel="Confirm ban"
            placeholder="Why is this user being banned? (audit-logged; shown to the user on appeal)"
            pending={banMut.isPending}
            error={banMut.error}
            danger
            onConfirm={(reason) => banMut.mutate({ userId: user.id, reason })}
          />
        )}
        <GrantCreditsForm
          pending={grantMut.isPending}
          error={grantMut.error}
          onSubmit={({ amount, reason }) => grantMut.mutate({ userId: user.id, amount, reason })}
        />
      </Section>

      <Section title="Recent credit ledger (10 most recent)">
        {recentLedger.length === 0 && (
          <p className="text-sm text-neutral-500">No ledger entries.</p>
        )}
        {recentLedger.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500">
              <tr>
                <th className="pb-1 font-medium">When</th>
                <th className="pb-1 font-medium">Delta</th>
                <th className="pb-1 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {recentLedger.map((row) => (
                <tr key={row.id} className="border-t border-neutral-100">
                  <td className="py-1 text-neutral-600">
                    {new Date(row.createdAt).toLocaleString()}
                  </td>
                  <td
                    className={`py-1 font-mono ${row.delta > 0 ? 'text-success-700' : 'text-danger-700'}`}
                  >
                    {row.delta > 0 ? `+${row.delta}` : row.delta}
                  </td>
                  <td className="py-1 text-neutral-700">{row.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section title="Full profile">
        {profileFull.isLoading && <p className="text-sm text-neutral-500">Loading profile…</p>}
        {profileFull.isError && (
          <p className="text-sm text-danger-600">Could not load full profile.</p>
        )}
        {profileFull.data && <FullProfileView data={profileFull.data} />}
      </Section>

      <Section title="Audit log (last 100 actions on this user)">
        {audit.isLoading && <p className="text-sm text-neutral-500">Loading…</p>}
        {audit.isError && <p className="text-sm text-danger-600">Could not load audit log.</p>}
        {audit.data && <AuditLogTable entries={audit.data.entries} />}
      </Section>

      <Section title="Recent reports">
        {recentReports.length === 0 && (
          <p className="text-sm text-neutral-500">No reports filed by or against this user.</p>
        )}
        {recentReports.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-left text-neutral-500">
              <tr>
                <th className="pb-1 font-medium">When</th>
                <th className="pb-1 font-medium">Direction</th>
                <th className="pb-1 font-medium">Reason</th>
                <th className="pb-1 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {recentReports.map((row) => (
                <tr key={`${row.direction}-${row.id}`} className="border-t border-neutral-100">
                  <td className="py-1 text-neutral-600">
                    {new Date(row.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-1 text-neutral-700">
                    {row.direction === 'filed_by' ? 'filed' : 'against'}
                  </td>
                  <td className="py-1 text-neutral-900">{row.reason}</td>
                  <td className="py-1 text-neutral-700">{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-sm font-medium text-neutral-700">{title}</h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

// Field groups for the full-profile view. Anything in `profiles` that
// isn't listed here drops into the "Other" bucket at the bottom — so
// new schema columns surface immediately without admin-app changes.
// Sensitive columns (wali email/phone tokens, raw geo) are excluded
// on purpose — the user-facing surface doesn't show them either, and
// they don't help spot the kind of issues this view is for. PII still
// flows through here (display name, dob, bio) — admin-auth gating is
// what protects it.
const FIELD_GROUPS: { title: string; keys: string[] }[] = [
  {
    title: 'Basics',
    keys: [
      'displayName',
      'dateOfBirth',
      'gender',
      'heightCm',
      'maritalStatus',
      'hasChildren',
      'childrenCount',
      'childrenCustody',
      'nationalityIso',
      'countryOfResidenceIso',
      'ethnicities',
      'languages',
      'motherTongue',
      'grewUpCountryIso',
      'grewUpRegion',
    ],
  },
  {
    title: 'Education & work',
    keys: ['educationLevel', 'educationDiscipline', 'profession', 'industry', 'employmentStatus'],
  },
  {
    title: 'Faith',
    keys: [
      'sect',
      'subSect',
      'religiosity',
      'prays',
      'fastsRamadan',
      'quranEngagement',
      'dressStyle',
      'diet',
      'alcohol',
      'smoking',
      'convertStatus',
      'hajjStatus',
      'umrahStatus',
      'quranMemorization',
      'mosqueAttendance',
    ],
  },
  {
    title: 'Marriage intent',
    keys: [
      'marriageTimeframe',
      'familyInvolvement',
      'wantsChildren',
      'relocation',
      'livingArrangement',
      'polygamyPreference',
      'seekingPace',
      'openAboutPast',
      'yearsSinceMaritalChange',
      'childrenLivingSituation',
    ],
  },
  {
    title: 'About',
    keys: ['bio', 'interests', 'personalityTraits', 'promptAnswers'],
  },
  {
    title: 'Family & financial',
    keys: [
      'incomeBracket',
      'currentlySupportsFamily',
      'financiallyIndependent',
      'mahrExpectation',
      'familyPractice',
      'familyEducationLevel',
      'familyResidence',
    ],
  },
  {
    title: 'Voice & video intro',
    keys: [
      'voiceIntroS3Key',
      'voiceIntroDurationMs',
      'voiceIntroModerationStatus',
      'voiceIntroModerationReason',
      'voiceIntroUploadedAt',
      'videoIntroS3Key',
      'videoIntroDurationMs',
      'videoIntroModerationStatus',
      'videoIntroModerationReason',
      'videoIntroUploadedAt',
    ],
  },
  {
    title: 'Privacy',
    keys: ['blurUntilMatch', 'paidApproachOnly'],
  },
];

// Columns the admin never needs to see for spotting issues — wali
// tokens, raw geo coords, derived embeddings, internal timestamps
// not load-bearing for triage.
const HIDDEN_KEYS = new Set([
  'userId',
  'waliConfirmationToken',
  'waliConfirmationSentAt',
  'homeGeo',
  'embedding',
  'embeddingUpdatedAt',
]);

function FullProfileView({ data }: { data: ProfileFull }) {
  const { profile, photos } = data;
  if (!profile && photos.length === 0) {
    return (
      <p className="text-sm text-neutral-500">
        No profile (user hasn't completed the wizard) and no photos.
      </p>
    );
  }

  const groupedKeys = new Set(FIELD_GROUPS.flatMap((g) => g.keys));
  const otherKeys = profile
    ? Object.keys(profile)
        .filter((k) => !groupedKeys.has(k) && !HIDDEN_KEYS.has(k))
        .sort()
    : [];

  return (
    <div className="space-y-5">
      {photos.length > 0 && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">
            Photos ({photos.length})
          </p>
          <ul className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {photos.map((p) => (
              <li
                key={p.id}
                className="rounded-md border border-neutral-200 bg-neutral-50 p-2 text-xs"
              >
                {p.clearUrl ? (
                  // The link target is the signed clear URL itself so
                  // the admin can open the full-resolution image in a
                  // new tab (right-click → save, etc. — needed for
                  // reviewing reported photos). The signed URL expires
                  // in 10 min; refreshing the dossier re-presigns.
                  <a href={p.clearUrl} target="_blank" rel="noopener noreferrer" className="block">
                    <img
                      src={p.thumbUrl ?? p.clearUrl}
                      alt={`Position ${p.position}`}
                      className="aspect-square w-full rounded object-cover"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <div className="flex aspect-square w-full items-center justify-center rounded bg-neutral-200 text-center text-neutral-500">
                    no signed URL
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-medium text-neutral-900">
                    Pos {p.position} · {p.widthPx}×{p.heightPx} · {p.moderationStatus}
                  </span>
                  <span className="text-neutral-500">
                    {new Date(p.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="mt-1 font-mono text-[10px] text-neutral-500 break-all">
                  {p.s3KeyOriginal}
                </p>
                {p.moderationReason && (
                  <p className="mt-1 text-neutral-700">Reason: {p.moderationReason}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {profile &&
        FIELD_GROUPS.map((group) => {
          const visible = group.keys.filter((k) => k in profile);
          if (visible.length === 0) return null;
          return (
            <FieldGroup key={group.title} title={group.title} keys={visible} profile={profile} />
          );
        })}

      {profile && otherKeys.length > 0 && (
        <FieldGroup title="Other" keys={otherKeys} profile={profile} />
      )}
    </div>
  );
}

function FieldGroup({
  title,
  keys,
  profile,
}: {
  title: string;
  keys: string[];
  profile: Record<string, unknown>;
}) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">{title}</p>
      <dl className="mt-1 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
        {keys.map((k) => (
          <FieldRow key={k} label={k} value={profile[k]} />
        ))}
      </dl>
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: unknown }) {
  return (
    <>
      <dt className="text-neutral-500">{label}</dt>
      <dd className="text-neutral-900 break-words">{formatValue(value)}</dd>
    </>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) {
    if (v.length === 0) return '—';
    return v
      .map((x) => (typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x)))
      .join(', ');
  }
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function AuditLogTable({ entries }: { entries: AuditLogEntry[] }) {
  if (entries.length === 0) {
    return <p className="text-sm text-neutral-500">No admin actions recorded against this user.</p>;
  }
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-neutral-500">
        <tr>
          <th className="pb-1 font-medium">When</th>
          <th className="pb-1 font-medium">Actor</th>
          <th className="pb-1 font-medium">Action</th>
          <th className="pb-1 font-medium">Details</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id} className="border-t border-neutral-100 align-top">
            <td className="py-1 pr-2 text-neutral-600 whitespace-nowrap">
              {new Date(e.createdAt).toLocaleString()}
            </td>
            <td className="py-1 pr-2 text-neutral-700">
              {e.actorEmail ?? (
                <span className="text-neutral-500">
                  {e.actorKind}
                  {e.actorId ? ` · ${e.actorId.slice(0, 8)}` : ''}
                </span>
              )}
            </td>
            <td className="py-1 pr-2 font-medium text-neutral-900">{e.action}</td>
            <td className="py-1 text-xs text-neutral-700 break-words">
              <PayloadCell payload={e.payload} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PayloadCell({ payload }: { payload: unknown }) {
  if (!payload || typeof payload !== 'object') return <span className="text-neutral-400">—</span>;
  const entries = Object.entries(payload as Record<string, unknown>);
  if (entries.length === 0) return <span className="text-neutral-400">—</span>;
  return (
    <span>
      {entries.map(([k, v], i) => (
        <span key={k}>
          {i > 0 ? ' · ' : ''}
          <span className="text-neutral-500">{k}</span>={' '}
          <span className="text-neutral-800">{formatValue(v)}</span>
        </span>
      ))}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: BadgeVariant =
    status === 'active' ? 'success' : status === 'banned' ? 'danger' : 'neutral';
  return <Badge variant={variant}>{status}</Badge>;
}

interface ReasonPromptButtonProps {
  label: string;
  confirmLabel: string;
  placeholder: string;
  pending: boolean;
  error: Error | null;
  danger?: boolean;
  onConfirm: (reason: string) => void;
}

function ReasonPromptButton({
  label,
  confirmLabel,
  placeholder,
  pending,
  error,
  danger,
  onConfirm,
}: ReasonPromptButtonProps) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (!open) {
    return (
      <div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={`rounded-md px-3 py-2 text-sm font-medium ${
            danger
              ? 'bg-danger-600 text-white hover:bg-danger-700'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {label}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-neutral-200 p-3">
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full rounded-md border border-neutral-300 p-2 text-sm"
        minLength={3}
        required
      />
      {error && <p className="text-sm text-danger-600">{(error as Error).message}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          disabled={pending || reason.trim().length < 3}
          onClick={() => {
            onConfirm(reason.trim());
            setOpen(false);
            setReason('');
          }}
          className={`rounded-md px-3 py-2 text-sm font-medium disabled:opacity-50 ${
            danger
              ? 'bg-danger-600 text-white hover:bg-danger-700'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {pending ? 'Working…' : confirmLabel}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setReason('');
          }}
          className="rounded-md border border-neutral-300 px-3 py-2 text-sm text-neutral-700"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

interface GrantCreditsFormProps {
  pending: boolean;
  error: Error | null;
  onSubmit: (input: { amount: number; reason: string }) => void;
}

function GrantCreditsForm({ pending, error, onSubmit }: GrantCreditsFormProps) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const parsed = Number.parseInt(amount, 10);
  const valid = Number.isFinite(parsed) && parsed !== 0 && reason.trim().length >= 3;

  return (
    <form
      className="mt-2 space-y-2 rounded-md border border-neutral-200 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        onSubmit({ amount: parsed, reason: reason.trim() });
        setAmount('');
        setReason('');
      }}
    >
      <label className="flex items-center gap-2 text-sm font-medium text-neutral-700">
        Credits
        <input
          type="number"
          inputMode="numeric"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="positive = grant, negative = revoke"
          className="w-72 rounded-md border border-neutral-300 px-2 py-1 text-sm"
        />
      </label>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Reason (audit-logged)"
        rows={2}
        className="w-full rounded-md border border-neutral-300 p-2 text-sm"
      />
      {error && <p className="text-sm text-danger-600">{(error as Error).message}</p>}
      <button
        type="submit"
        disabled={pending || !valid}
        className="rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
      >
        {pending ? 'Submitting…' : 'Apply'}
      </button>
    </form>
  );
}
